/**
 * buildMorphUrl — Constructs the iframe src URL for morph-viewer-v2.html
 * Handles three camerasPath formats:
 *   1. Full blob URL:   https://...blob.../section_0/cameras.json
 *   2. API proxy URL:   /api/scan-data/shopId/section_0/cameras.json
 *   3. Relative path:   shopId/section_0/cameras.json
 */
import { apiUrl } from '@/config'

export interface DatasetWithPaths {
  id: string
  paths?: {
    cameras?: string
    [key: string]: any
  }
}

/**
 * Build morph viewer URL from a cameras.json path (any format).
 * Fetches SAS token for direct blob access; falls back to API proxy.
 */
export async function buildMorphUrl(dataset: DatasetWithPaths): Promise<string> {
  const paths = dataset.paths
  const camerasPath = paths?.cameras || `/api/scan-data/${dataset.id}/section_0/cameras.json`
  return buildMorphUrlFromCamerasPath(camerasPath)
}

/**
 * Build morph viewer URL from a frames directory path.
 * Used after loadDataset sets currentSectionFramesPath for the best section.
 * E.g. framesPath = 'https://...section_2/frames/' → base = 'https://...section_2/'
 */
export async function buildMorphUrlFromBase(base: string): Promise<string> {
  // base is the section directory (containing cameras.json)
  return buildMorphUrlFromCamerasPath(base + 'cameras.json')
}

async function buildMorphUrlFromCamerasPath(camerasPath: string): Promise<string> {
  // ── Case 1: Full blob URL ─────────────────────────────────────────────────
  if (camerasPath.startsWith('https://') || camerasPath.startsWith('http://')) {
    const base = camerasPath.substring(0, camerasPath.lastIndexOf('/') + 1)
    try {
      const res = await fetch(apiUrl('/api/scan-data-token'))
      if (res.ok) {
        const { sas_token } = await res.json()
        const params = new URLSearchParams({ base, sas: sas_token })
        return `/morph-viewer/morph-viewer-v2.html?${params.toString()}`
      }
    } catch { /* fall through */ }
    // No SAS — return with just base (blob may allow anonymous read)
    return `/morph-viewer/morph-viewer-v2.html?base=${encodeURIComponent(base)}`
  }

  // ── Case 2: API proxy URL (/api/scan-data/...) or relative path ────────────
  const apiPrefix = '/api/scan-data/'
  const blobRelative = camerasPath.startsWith(apiPrefix)
    ? camerasPath.substring(apiPrefix.length, camerasPath.lastIndexOf('/') + 1)
    : camerasPath.substring(0, camerasPath.lastIndexOf('/') + 1)

  try {
    const res = await fetch(apiUrl('/api/scan-data-token'))
    if (res.ok) {
      const { base_url, sas_token } = await res.json()
      const base = `${base_url}/${blobRelative}`
      const params = new URLSearchParams({ base, sas: sas_token })
      return `/morph-viewer/morph-viewer-v2.html?${params.toString()}`
    }
  } catch { /* fall through */ }

  // Fallback: API proxy (slower but always works)
  const base = apiUrl(`${apiPrefix}${blobRelative}`)
  return `/morph-viewer/morph-viewer-v2.html?base=${encodeURIComponent(base)}`
}
